import { useCallback, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useAppSelector } from '@/redux/hooks'
import { sendChat } from '@/infrastructure/api/chat'
import type { ChatpbChatMessage } from '@itsrever/rever-api'
import { useChatContext } from './chat-context'
import type { ChatFollowupKind, ChatScreenRegistration } from './types'
import { ANALYTICS_TAB, getAnalyticsMcpAccessToken } from './analytics-mcp-token'
import {
  useAssistantTracking,
  type ChatMessageSource
} from './use-assistant-tracking'

export interface UseChat {
  messages: ChatpbChatMessage[]
  send: (text: string, source?: ChatMessageSource) => void
  retry: () => void
  reset: () => void
  isPending: boolean
  error: Error | null
  // Indexes into `messages` for assistant turns that updated the screen draft.
  rulesUpdatedIndexes: Set<number>
  // Actions the assistant offered under its latest reply.
  followups: ChatFollowupKind[]
}

const FOLLOWUP_KINDS: readonly ChatFollowupKind[] = ['export']

// `result` is an untyped Struct: keep only kinds this build can label, so a newer
// backend adding one never renders an unlabelled chip here.
function parseFollowups(result: unknown): ChatFollowupKind[] {
  const raw = (result as { followups?: unknown } | undefined)?.followups
  if (!Array.isArray(raw)) return []
  const kinds = raw
    .map((entry) => (entry as { kind?: unknown } | null)?.kind)
    .filter((kind): kind is ChatFollowupKind =>
      FOLLOWUP_KINDS.includes(kind as ChatFollowupKind)
    )
  return [...new Set(kinds)]
}

// current_tab plus the screen's own state. The panel disables sending unless a
// screen is registered, so a context is normally present; the null branch is a
// defensive fallback that would yield an empty payload.
function buildPayload(context: ChatScreenRegistration | null): object {
  const state = context?.getPayload()
  const rest =
    state && typeof state === 'object' ? (state as Record<string, unknown>) : {}
  return { current_tab: context?.tab, ...rest }
}

// A send snapshots the context + ecommerce it was fired from, so an in-flight
// request applies its result to the right screen even if the user navigates.
interface SendVars {
  history: ChatpbChatMessage[]
  ctx: ChatScreenRegistration | null
  ecommerceId: string
}

export function useChat(): UseChat {
  // messages/rulesUpdatedIndexes live in the provider so they survive the panel
  // unmounting on close (see chat-context).
  const {
    activeContext,
    messages,
    setMessages,
    rulesUpdatedIndexes,
    setRulesUpdatedIndexes,
    setFeedbackByIndex,
    followups,
    setFollowups
  } = useChatContext()
  const { getAccessTokenSilently } = useAuth0()
  const { trackMessageSent } = useAssistantTracking()
  // The chat is gated behind a present ecommerce_id (same flag source), so this
  // is always set when the panel is reachable; coerce to satisfy the API type.
  const ecommerceId =
    useAppSelector((s) => s.settingsApi.settings.response.ecommerce_id) ?? ''

  // Mirror state in refs so `send`/`retry` stay stable and read the freshest
  // history without depending on it (the composer is disabled while pending, so
  // only one send is ever in flight).
  const messagesRef = useRef<ChatpbChatMessage[]>(messages)
  messagesRef.current = messages
  const lastVarsRef = useRef<SendVars | null>(null)

  const {
    mutate,
    isPending,
    error,
    reset: resetMutation
  } = useMutation({
    mutationFn: async ({ history, ctx, ecommerceId }: SendVars) => {
      const mcpAccessToken =
        ctx?.tab === ANALYTICS_TAB
          ? await getAnalyticsMcpAccessToken(getAccessTokenSilently)
          : undefined
      return sendChat({ ecommerceId, messages: history, payload: buildPayload(ctx), mcpAccessToken })
    },
    onSuccess: (response, { history, ctx }) => {
      const assistantIndex = history.length
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: response.reply ?? '' }
      ])
      setFollowups(parseFollowups(response.result))
      if (response.action === 'rules_updated') {
        // Apply to the context the message was sent from, not whatever is active
        // now (the user may have navigated while the request was in flight), and
        // only confirm when that screen actually applied it.
        const applied = ctx?.applyResult?.(response.result) ?? false
        if (applied) {
          setRulesUpdatedIndexes((prev) => new Set(prev).add(assistantIndex))
        }
      }
    }
  })

  const send = useCallback(
    (text: string, source: ChatMessageSource = 'composer') => {
      const content = text.trim()
      if (!content) return
      trackMessageSent({
        content,
        source,
        turnIndex: messagesRef.current.length
      })
      const history: ChatpbChatMessage[] = [
        ...messagesRef.current,
        { role: 'user', content }
      ]
      const vars: SendVars = { history, ctx: activeContext, ecommerceId }
      lastVarsRef.current = vars
      setMessages(history)
      // The previous reply's offer no longer applies to the answer being fetched.
      setFollowups([])
      mutate(vars)
    },
    [
      mutate,
      activeContext,
      ecommerceId,
      setMessages,
      setFollowups,
      trackMessageSent
    ]
  )

  const retry = useCallback(() => {
    if (lastVarsRef.current) mutate(lastVarsRef.current)
  }, [mutate])

  // Start a fresh conversation from zero. The draft the chat may have applied to
  // the screen is left as-is — this only clears the message thread. Also reset
  // the mutation so a prior error/retry does not linger over the empty thread.
  const reset = useCallback(() => {
    setMessages([])
    setRulesUpdatedIndexes(new Set())
    setFeedbackByIndex(new Map())
    setFollowups([])
    lastVarsRef.current = null
    resetMutation()
  }, [
    setMessages,
    setRulesUpdatedIndexes,
    setFeedbackByIndex,
    setFollowups,
    resetMutation
  ])

  return {
    messages,
    send,
    retry,
    reset,
    isPending,
    error,
    rulesUpdatedIndexes,
    followups
  }
}
